/**
 * Rig materials, built on CC0 photogrammetry scans from ambientCG.
 *
 * These were previously generated from noise. That was a mistake: procedural noise is the
 * right tool for *fields* (foam, wakes, flipbooks) but it cannot reproduce the correlated
 * albedo/normal/roughness detail of real corroded steel, and no amount of parameter
 * tuning closes that gap. ASSET_AND_LICENSE_PLAN.md section 3 names ambientCG as the
 * primary PBR source; `npx tsx tools/fetchAssets.ts` acquires them with licence proof.
 *
 * Sources are CC0 1.0 — no attribution obligation, though every one is recorded in
 * docs/licenses.csv with its API response archived as provenance.
 */

import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { PBRMetallicRoughnessMaterial } from '@babylonjs/core/Materials/PBR/pbrMetallicRoughnessMaterial';
import { Constants } from '@babylonjs/core/Engines/constants';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import type { BaseTexture } from '@babylonjs/core/Materials/Textures/baseTexture';
import type { Scene } from '@babylonjs/core/scene';

import { MistPlugin } from '../mistPlugin';
import { maxSimultaneousLightsFor } from '../lightBudget';

const TEX_ROOT = 'textures';

/*
 * The per-mesh light count now lives in ../lightBudget: twelve where the GPU's uniform-
 * block limit allows it, budgeted down where it does not (launch day proved twelve
 * cannot even LINK on minimum-spec WebGL2 — each light is a uniform buffer, and twelve
 * plus Babylon's three fixed buffers exceeds the guaranteed dozen). rigBuilder budgets
 * lamp creation against the same function, so the two figures cannot drift apart —
 * drift is what once left sixty-odd inert lights making the rig read as dark.
 *
 * Each step up costs a shader permutation and per-pixel work. Do not raise the ceiling
 * without measuring; the way to light more of the rig is more meshes, not a higher cap.
 */

/**
 * The simple metallic-roughness material intentionally hides a separate opacity slot.
 * The rig's one hero cutout surface needs it so photographic albedo and a hard grating
 * mask can coexist. Exposing the inherited slot here also makes shadow alpha testing use
 * the mask instead of looking for a nonexistent alpha channel in the colour JPEG.
 */
class RigPBRMaterial extends PBRMetallicRoughnessMaterial {
  setOpacityMask(mask: BaseTexture): void {
    this._opacityTexture = mask;
    this._markAllSubMeshesAsTexturesAndMiscDirty();
  }

  override getAlphaTestTexture(): BaseTexture | null {
    return this._opacityTexture ?? super.getAlphaTestTexture();
  }
}

/** Every scan now carries a glTF-style packed map: R=AO, G=roughness, B=metalness. */
interface ScanMaps {
  color?: Texture;
  normal: Texture;
  orm: Texture;
  opacity?: Texture;
}

function loadScan(
  scene: Scene,
  folder: string,
  options: { color?: boolean; colorName?: string; opacity?: boolean } = {},
): ScanMaps {
  const mk = (name: string, extension: 'jpg' | 'png', gammaSpace: boolean): Texture => {
    const t = new Texture(
      `${TEX_ROOT}/${folder}/${name}.${extension}`,
      scene,
      false,
      false,
      Texture.TRILINEAR_SAMPLINGMODE,
    );
    // Colour is authored in sRGB. Normal, ORM and opacity values are measurements and
    // must remain linear or their thresholds and physical response are distorted.
    t.gammaSpace = gammaSpace;
    t.wrapU = Constants.TEXTURE_WRAP_ADDRESSMODE;
    t.wrapV = Constants.TEXTURE_WRAP_ADDRESSMODE;
    // Vertex UVs already carry real-world metres and material-specific tile density.
    // Keeping texture transforms neutral makes that the only scale authority.
    t.uScale = 1;
    t.vScale = 1;
    // Structural members are viewed at extreme grazing angles across the whole frame;
    // anisotropy is what keeps their surfaces from smearing into mush at distance.
    t.anisotropicFilteringLevel = 8;
    return t;
  };
  const maps: ScanMaps = {
    normal: mk('normal', 'jpg', false),
    orm: mk('orm', 'jpg', false),
  };
  if (options.color !== false) maps.color = mk(options.colorName ?? 'color', 'jpg', true);
  if (options.opacity) {
    const opacity = mk('opacity', 'png', false);
    // The derived mask is single-channel: white metal, black holes.
    opacity.getAlphaFromRGB = true;
    opacity.hasAlpha = true;
    maps.opacity = opacity;
  }
  return maps;
}

/**
 * Babylon reads glTF metallic-roughness directly: roughness from G and metalness from B.
 * The same packed map can feed occlusion from R through the material's grayscale
 * occlusion slot. The metallic scalar remains useful as a physical override: painted
 * rails and concrete stay dielectric, while exposed steel preserves the scan's B channel.
 */
function buildMaterial(
  scene: Scene,
  name: string,
  maps: ScanMaps,
  tint: Color3,
  opts: {
    metallic?: number;
    normalStrength?: number;
    occlusionStrength?: number;
  } = {},
): RigPBRMaterial {
  const m = new RigPBRMaterial(name, scene);
  m.baseTexture = maps.color ?? null;
  m.baseColor = tint;
  m.normalTexture = maps.normal;
  maps.normal.level = opts.normalStrength ?? 1;
  m.metallicRoughnessTexture = maps.orm;
  m.occlusionTexture = maps.orm;
  m.roughness = 1;
  m.metallic = opts.metallic ?? 1;
  // Keep texture AO subordinate to the scene's short-range SSAO.
  m.occlusionStrength = opts.occlusionStrength ?? 0.72;
  // The rig uses a sun/sky pair plus a small hierarchy of local floodlights. Babylon's
  // default four-light material limit silently dropped most of the warm local fills,
  // leaving the night structure black around a few emissive lenses.
  /*
   * Raised from 6.
   *
   * This is the real reason the rig read as dark with 144 lights in the scene: the whole
   * platform is accumulated into about five meshes, and this cap is PER MESH — so one
   * mesh spanning the entire structure was being lit by six lamps total, however many
   * existed. Every other light was uploaded, sorted, and then discarded by the shader.
   *
   * Each step up costs a shader permutation and per-pixel work, so this is not free and
   * should not be raised further without measuring.
   */
  m.maxSimultaneousLights = maxSimultaneousLightsFor(scene);

  /*
   * Every surface breathes the same air.
   *
   * This was missing, and on the rig it barely showed — structure a hundred metres away
   * has almost no air in front of it. On a vessel five kilometres out it showed
   * completely: the hull rendered at full contrast as a hard black cutout pasted over
   * fog, while the sea behind it had faded to nothing. Atmospheric perspective is the
   * whole mechanism by which distance reads (plan C9), and a ship is the object that
   * needs it most.
   */
  new MistPlugin(m);
  return m;
}

export interface RigMaterials {
  /** Weathered ochre paint — handrails, boom chords, kerbs. */
  yellowPaint: PBRMetallicRoughnessMaterial;
  /** Corroded structural steel — legs, trusses, bracing. */
  steel: PBRMetallicRoughnessMaterial;
  /** Darker steel — modules, housings, equipment. */
  darkSteel: PBRMetallicRoughnessMaterial;
  /** Weathered plate — decks, upright process vessels, and cladding. */
  grating: PBRMetallicRoughnessMaterial;
  /** Open walkway grating for the observation platform. */
  openGrating: PBRMetallicRoughnessMaterial;
  /**
   * Near-black machined steel. Additive: used only by the crane's hook block, where the
   * general dark steel reads too light against open sky and water.
   */
  machinedSteel: PBRMetallicRoughnessMaterial;
}

export function createRigMaterials(scene: Scene): RigMaterials {
  // The scan's white paint has been colourised into a compact yellow derivative while
  // retaining its real chips, scratches and exposed rust. This preserves photographic
  // wear without multiplying the raw runoff map into a timber-like brown rail.
  const painted = loadScan(scene, 'steel-painted', { colorName: 'paint-chipped' });
  const plate = loadScan(scene, 'steel-plate');
  const walk = loadScan(scene, 'walkway', { opacity: true });
  const worn = loadScan(scene, 'steel-worn');

  // Painted safety rails are dielectric even though the source scan's metal map is
  // mostly white. The small scalar deliberately overrides that source channel.
  const yellowPaint = buildMaterial(scene, 'rigYellow', painted, new Color3(0.98, 0.94, 0.84), {
    metallic: 0.03,
    normalStrength: 0.46,
  });
  // Neutral worn steel is the structural base. The old directional rust scan turned
  // long braces and vessels into timber; oxidation now comes from localized rig detail
  // and the plate/walkway roles instead of coating every primary member.
  const steel = buildMaterial(scene, 'rigSteel', worn, new Color3(0.84, 0.88, 0.92), {
    metallic: 0.26,
    normalStrength: 0.72,
  });
  const machinedSteel = buildMaterial(scene, 'rigMachinedSteel', worn, new Color3(0.20, 0.22, 0.25), {
    metallic: 0.3,
  });
  const darkSteel = buildMaterial(scene, 'rigDarkSteel', worn, new Color3(0.56, 0.60, 0.64), {
    metallic: 0.30,
    normalStrength: 0.72,
  });
  const grating = buildMaterial(scene, 'rigPlate', plate, new Color3(0.54, 0.57, 0.60), {
    metallic: 0.28,
    normalStrength: 0.74,
  });
  const openGrating = buildMaterial(scene, 'rigOpenGrating', walk, new Color3(0.32, 0.34, 0.36), {
    metallic: 0.38,
  });
  if (walk.opacity) {
    openGrating.setOpacityMask(walk.opacity);
    openGrating.alphaCutOff = 0.46;
    openGrating.transparencyMode = PBRMetallicRoughnessMaterial.PBRMATERIAL_ALPHATEST;
  }
  openGrating.doubleSided = true;

  return { yellowPaint, steel, darkSteel, grating, openGrating, machinedSteel };
}

/**
 * Hull material for vessels. Additive — the rig's own set is untouched.
 *
 * Ships carry their colour in vertex data (each hull bakes its own palette), so this is
 * deliberately a white-tinted surface whose only job is to supply photographic normal and
 * roughness detail. One material for the whole fleet keeps every vessel in the scene to a
 * single draw per hull and reuses textures Babylon has already uploaded for the rig.
 */
export function createHullMaterial(scene: Scene): PBRMetallicRoughnessMaterial {
  const worn = loadScan(scene, 'steel-worn');
  const m = buildMaterial(scene, 'vesselHull', worn, new Color3(1, 1, 1), {
    // Painted ship plating is dielectric. High metallic here would render hulls black
    // wherever the environment cube is dim — the pitfall this project has hit before.
    metallic: 0.12,
    normalStrength: 0.62,
  });
  return m;
}

/** Emissive material for floodlight lenses and warning lamps. */
export function createLampMaterial(
  scene: Scene,
  color: Color3,
  intensity: number,
): PBRMetallicRoughnessMaterial {
  const m = new PBRMetallicRoughnessMaterial('lamp', scene);
  m.baseColor = new Color3(0.02, 0.02, 0.02);
  m.emissiveColor = color.scale(intensity);
  m.roughness = 0.35;
  m.metallic = 0;
  // Emissives mist too: a floodlight 200m away must dim inside the air, not cut through it.
  new MistPlugin(m);
  return m;
}
